import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getMarket, getBranch, getShowroom, getServiceInfo } from '../../../../actions/location-actions';
import PropTypes from 'prop-types';
import { Row, Column, Button, Select, TextArea, TextInput, Checkbox, Icon } from '@pella/sash-framework';
import { savePostalCode, setCookie } from '../../../utilities/functions';
import EloquaForm from '../../eloqua-form';
import styles from './index.module.scss';
import { navigate } from 'gatsby';
import { isValidEmail, isValidPhoneNumber, isValidPostal } from '../../../utilities/validations';
import { useMutation } from '@apollo/client';
import { SUBMIT_PRO_QUOTE_FORM } from '../../../../enums/graphql';
import { useRecaptcha } from '../../../../hooks/use-recaptcha';
import Recaptcha from '../recaptcha';

const RequestProjectQuote = ({ path, utm, redirect, className }) => {
	const dispatch = useDispatch({});
	const [values, setValues] = useState({});
	const [errors, setErrors] = useState({});
	const [disabled, setDisabled] = useState(false);
	const [success, setSuccess] = useState(false);
	const [update, setUpdate] = useState(false);
	const [referralUrl, setReferralUrl] = useState((typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('referral')) ?? path);

	const { captchaToken, setCaptchaToken } = useRecaptcha();

	const [submitForm] = useMutation(SUBMIT_PRO_QUOTE_FORM, {
		onCompleted: () => {
			setSuccess(true);
			setDisabled(false);
			setCookie('ReqProjectQuote', true);
			navigate(redirect);
		},
		onError: (error) => {
			setDisabled(false);
			setErrors({ form: error.message });
		},
	});

	useEffect(() => {
		if (update) {
			setUpdate(false);
		}
	}, [update]);

	const onInputChange = (e) => {
		setValues({
			...values,
			[e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
		});
		setDisabled(false);
		setSuccess(false);
		setUpdate(true);
	};

	const onEmailBlur = (e) => {
		let errors = [];

		if (!isValidEmail(e.target.value)) {
			errors.emailAddress = 'Please enter a valid email address';
		}

		setErrors(errors);
	};

	const validateForm = (firstName, lastName, emailAddress, singleLineText, dropdownMenu3, mobilePhone, zipPostal) => {
		let errors = [];

		if (!firstName) {
			errors.firstName = 'Please enter your first name';
		}

		if (!lastName) {
			errors.lastName = 'Please enter your last name';
		}

		if (!isValidEmail(emailAddress)) {
			errors.emailAddress = 'Please enter a valid email address';
		}

		if (!singleLineText) {
			errors.singleLineText = 'Please enter a company name';
		}

		if (!dropdownMenu3) {
			errors.dropdownMenu3 = 'Please select a primary business type';
		}

		if (!isValidPhoneNumber(mobilePhone)) {
			errors.mobilePhone = 'Please enter a valid phone number';
		}

		if (!isValidPostal(zipPostal)) {
			errors.zipPostal = 'Please enter a valid postal code';
		}

		if (!captchaToken) {
			errors.recaptcha = 'You must complete the Captcha';
		}

		return errors;
	};

	const onFormSubmit = async (e) => {
		e.preventDefault();

		const formElement = e.target;
		const hiddenFields = Array.from(formElement.elements)
			.filter((el) => el.type === 'hidden')
			.reduce((acc, el) => {
				acc[el.name] = el.value;
				return acc;
			}, {});

		const errors = validateForm(values.firstName, values.lastName, values.emailAddress, values.singleLineText, values.dropdownMenu3, values.mobilePhone, values.zipPostal);

		if (Object.keys(errors).length > 0) {
			setErrors(errors);
		} else {
			setErrors({});
			setDisabled(true);
			savePostalCode(values.zipPostal);
			dispatch(getMarket(values.zipPostal));
			dispatch(getBranch(values.zipPostal));
			dispatch(getShowroom(values.zipPostal));
			dispatch(getServiceInfo(values.zipPostal));
			setUpdate(true);

			const utmFields = ['utmCampaign', 'utmSource', 'utmMedium', 'utmContent', 'utmTerm'];
			const utmData = utmFields.reduce((acc, field) => {
				if (hiddenFields[field]) {
					acc[field] = hiddenFields[field];
				}
				return acc;
			}, {});

			const input = {
				data: {
					elqFormName: hiddenFields.elqFormName,
					elqSiteID: hiddenFields.elqSiteID,
					elqCustomerGUID: hiddenFields.elqCustomerGUID,
					elqCookieWrite: parseInt(hiddenFields.elqCookieWrite || '1', 10),
					env: hiddenFields.env,
					url: referralUrl || '',
					recaptchaResponse: captchaToken,
					firstName: values.firstName,
					lastName: values.lastName,
					emailAddress: values.emailAddress,
					singleLineText: values.singleLineText,
					dropdownMenu3: values.dropdownMenu3,
					mobilePhone: values.mobilePhone,
					zipPostal: values.zipPostal,
					dropdownMenu: values.dropdownMenu || null,
					dropdownMenu2: values.dropdownMenu2 || null,
					paragraphText: values.paragraphText || null,
					singleCheckbox: values.singleCheckbox ? 'true' : 'false',
					...utmData,
				}
			};

			try {
				await submitForm({ variables: { input } });
			} catch (error) {
				setDisabled(false);
			}
		}
	};

	return (
		<EloquaForm
			elqFormName='UntitledForm-1565383147079'
			onFormSubmit={(e) => onFormSubmit(e)}
			elqCookieWrite={1}
			className={`${styles.requestQuote} ${className || ''}`}
			utm={utm}
		>
			<input
				type='hidden'
				name='url'
				value={referralUrl}
			/>

			<Row>
				<Column
					xs={12}
					md={6}
				>
					<TextInput
						id='firstName'
						labelText='First Name'
						onChange={(e) => onInputChange(e)}
						required={true}
						error={errors.firstName}
						errorText={errors.firstName}
						autoComplete='given-name'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
				>
					<TextInput
						id='lastName'
						labelText='Last Name'
						onChange={(e) => onInputChange(e)}
						required={true}
						error={errors.lastName}
						errorText={errors.lastName}
						autoComplete='family-name'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
				>
					<TextInput
						id='emailAddress'
						labelText='Email Address'
						onChange={(e) => onInputChange(e)}
						onBlur={(e) => onEmailBlur(e)}
						required={true}
						error={errors.emailAddress}
						errorText={errors.emailAddress}
						autoComplete='email'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
				>
					<TextInput
						id='singleLineText'
						labelText='Company Name'
						onChange={(e) => onInputChange(e)}
						required={true}
						error={errors.singleLineText}
						errorText={errors.singleLineText}
						autoComplete='organization'
					/>
				</Column>

				<Column xs={12}>
					<Select
						id='dropdownMenu3'
						labelText='Primary Business Type'
						onChange={(e) => onInputChange(e)}
						required={true}
						error={errors.dropdownMenu3}
						errorText={errors.dropdownMenu3}
						autoComplete='off'
					>
						<option value=''>-- Select an option --</option>
						<option value='Replacement Contractor'>Replacement Contractor</option>
						<option value='Remodeling Contractor'>Remodeling Contractor</option>
						<option value='Residential Builder/GC'>Residential Builder/GC</option>
						<option value='Commercial Builder/GC'>Commercial Builder/GC</option>
						<option value='Architectural/Design Firm'>Architectural / Design Firm</option>
						<option value='Commercial Building Owner'>Commercial Building Owner</option>
						<option value='Homeowner'>Homeowner</option>
					</Select>
				</Column>

				<Column
					xs={12}
					md={6}
				>
					<TextInput
						type='tel'
						id='mobilePhone'
						labelText='Mobile Phone'
						onChange={(e) => onInputChange(e)}
						format='(###) ###-####'
						required
						allowEmptyFormatting
						error={errors.mobilePhone}
						errorText={errors.mobilePhone}
						autoComplete='tel-national'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
				>
					<TextInput
						id='zipPostal'
						labelText='Project Zip or Postal Code'
						onChange={(e) => onInputChange(e)}
						required={true}
						error={errors.zipPostal}
						errorText={errors.zipPostal}
						autoComplete='postal-code'
					/>
				</Column>

				<Column
					xs={12}
					md={6}
				>
					<Select
						id='dropdownMenu'
						labelText='# of Windows'
						onChange={(e) => onInputChange(e)}
						autoComplete='off'
					>
						<option value='0'>0</option>
						<option value='1-3'>1-3</option>
						<option value='4-6'>4-6</option>
						<option value='7-10'>7-10</option>
					</Select>
				</Column>

				<Column
					xs={12}
					md={6}
				>
					<Select
						id='dropdownMenu2'
						labelText='# of Doors'
						onChange={(e) => onInputChange(e)}
						autoComplete='off'
					>
						<option value='0'>0</option>
						<option value='1-3'>1-3</option>
						<option value='4-6'>4-6</option>
						<option value='7-10'>7-10</option>
					</Select>
				</Column>

				<Column xs={12}>
					<TextArea
						id='paragraphText'
						labelText='Additional Details'
						onChange={(e) => onInputChange(e)}
						autoComplete='off'
					/>
				</Column>

				<Column xs={12}>
					<Checkbox
						id='singleCheckbox'
						labelText='Send me special offers and announcements via email'
						onChange={(e) => onInputChange(e)}
					/>
				</Column>

				<Column
					xs={12}
					className='mt-2.5'
				>
					<Recaptcha
						token={[captchaToken, setCaptchaToken]}
						source='PCCQ'
					/>
					{errors.recaptcha && (
						<p className='mt-0.5 flex items-center gap-1 text-xs text-red'>
							<Icon
								name='warning'
								size={16}
							/>
							{errors.recaptcha}
						</p>
					)}
				</Column>

				<Column xs={12}>
					<Button
						color='primary-alt'
						type='submit'
						wide={true}
						disabled={disabled}
					>
						{disabled ? 'Processing...' : 'Get A Quote'}
					</Button>
				</Column>

				{success && (
					<Column xs={12}>
						<div className={styles.serviceRequest_confirmationMsg}>Thanks for requesting a project quote! We have received your submission and will get in touch soon.</div>
					</Column>
				)}
			</Row>
		</EloquaForm>
	);
};

RequestProjectQuote.propTypes = {
	path: PropTypes.string,
	utm: PropTypes.object,
	redirect: PropTypes.string,
	className: PropTypes.string,
};

export default RequestProjectQuote;
